<template>
  <Card
    :pt="{
      root: {
        style:
          'height: 35rem; width: 30rem; user-select: text; padding: 16px; box-shadow: none; border: 1px solid #e5e7eb',
      },
      body: {
        style: 'padding: 0;',
      },
      content: {
        style: 'padding: 0;',
      },
      header: {
        style: 'height: 10px;',
      },
    }"
  >
    <template #header>
      <div v-if="isWIP" :class="$style.actionRow">
        <VdIcon store="fa" name="grip-lines-vertical" size="large" color="var(--vd-color-blue-dark-1)" />
        <ShopItemDropDownMenu
          :is-editable="isWIP"
          @edit="handleEditShopItem"
          @delete="deleteShopItem"
          @duplicate="duplicateShopItem"
        />
      </div>
    </template>
    <template #content>
      <div :class="$style.cardWrapper">
        <div :class="$style.iconWrapper">
          <template v-if="item.reward?.sprite">
            <img :class="$style.shopIcon" :src="item.reward?.sprite?.imageUrl" :alt="item.reward.sprite?.name" />
          </template>
          <template v-else>
            <VdIcon store="fa" name="image-slash" size="medium" :class="$style.shopIcon"></VdIcon>
          </template>
        </div>
        <div :class="$style.row">
          <div :class="$style.column">
            <p :class="$style.itemName">{{ item.name }}</p>
            <p v-if="item.technicalName" :class="[$style.itemName, $style.technicalName]">{{ item.technicalName }}</p>
            <p :class="[$style.itemName, $style.itemId]">
              {{ item.id }}
              <CopyButton :id="item.id" />
            </p>
          </div>
          <div :class="$style.column">
            <span v-if="item.purchaseLimit > 0" :class="$style.blueChip">
              <VdIcon store="fa" size="medium" name="shopping-cart" color="var(--vd-color-blue-default)"></VdIcon>
              {{ item.purchaseLimit }}
            </span>
            <span v-if="item.coolDown.hours > 0 || item.coolDown.days > 0" :class="$style.blueChip">
              <VdIcon store="fa" size="medium" name="clock" color="var(--vd-color-blue-default)"></VdIcon>
              {{ item.coolDown.days }}d{{ item.coolDown.hours }}h
            </span>
          </div>
        </div>
        <div v-if="item.reward?.items?.length > 0" :class="$style.rewardSection">
          <p :class="$style.label">Rewards</p>
          <div :class="$style.contentWrapper">
            <div v-for="rewardItem in item.reward.items.slice(0, 3)" :key="rewardItem.id" :class="$style.rewardWrapper">
              <span>
                <template v-if="rewardItem.sprite">
                  <img :class="$style.rewardIcon" :src="rewardItem.sprite?.imageUrl" :alt="item.reward?.name" />
                </template>
                <span :class="$style.rewardName">
                  {{ rewardItem.name }}
                </span>
              </span>
              <span>
                <VdIcon store="fa" size="medium" name="times"></VdIcon>
              </span>
              <span>{{ rewardItem.quantity }}</span>
            </div>
            <VdTooltip :content="rewardsTooltipValues" orientation="bottom">
              <div v-if="item.reward.items.length > 3" :class="[$style.rewardName, $style.rewardTooltip]">
                {{ `+${item.reward.items.length - 3}` }}
              </div>
            </VdTooltip>
          </div>
        </div>
        <div v-if="item.price" :class="$style.tagWrapper">
          <p :class="$style.label">Price</p>
          <div :class="$style.priceWrapper">
            <ShopPrice :price="item.price" />
          </div>
        </div>
        <div v-if="item.tagNames.length > 0" :class="$style.tagWrapper">
          <p :class="$style.label">Tags</p>
          <div :class="$style.contentWrapper">
            <Chip
              v-for="tag in item.tagNames.slice(0, 2)"
              :key="tag"
              :label="tag"
              :class="[$style.tag, $style.truncatedTag]"
              :pt="{
                label: {
                  class: $style.tagLabel,
                },
              }"
            />
            <VdTooltip :content="tooltipValues" orientation="bottom">
              <Chip
                v-if="item.tagNames.length > 2"
                :label="`+${item.tagNames.length - 2}`"
                :class="$style.tag"
                :pt="{
                  label: {
                    class: $style.tagLabel,
                  },
                }"
              />
            </VdTooltip>
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdTooltip } from "@voodoo.io/voodoo-ui";
import Card from "primevue/card";
import Chip from "primevue/chip";
import { computed } from "vue";

import CopyButton from "@/components/CopyButton/CopyButton.vue";
import ShopPrice from "@/modules/Store/components/ShopPrice.vue";
import type { ShopItem } from "@/modules/Store/shop.types";
import ShopItemDropDownMenu from "./ShopItemDropDownMenu.vue";

const props = defineProps<{
  item: ShopItem;
  versionId: string;
}>();

const emits = defineEmits(["edit", "delete", "duplicate"]);

const tooltipValues = computed(() => props.item.tagNames.map((tag) => `<div>${tag}</div>`).join(""));

const rewardsTooltipValues = computed(() =>
  props.item.reward.items.map((item) => `<div>${item.name} x ${item.quantity}</div>`).join("")
);

const isWIP = computed(() => props.versionId === "wip");

const handleEditShopItem = () => {
  emits("edit", props.item.id);
};

const duplicateShopItem = () => {
  emits("duplicate", props.item.id);
};

const deleteShopItem = () => {
  emits("delete", props.item.id);
};
</script>

<style module lang="scss">
.cardWrapper {
  display: flex;
  flex-direction: column;
  color: #303133;
  padding: 1rem;
}

.iconWrapper {
  text-align: center;
}

.shopIcon {
  height: 40px;
  width: 40px;
  margin-bottom: 0.5rem;
}

.label {
  color: var(--vd-color-grey-light-1);
  margin: auto;
  width: 8rem;
}

.itemName {
  margin: 0;
  font-size: 1.1rem;
  font-weight: bolder;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.technicalName {
  color: var(--vd-color-grey-light-1);
  font-weight: initial;
  font-size: 1rem;
}

.itemId {
  font-size: 0.8rem;
  color: var(--vd-color-grey-light-1);
  margin-top: 0.1rem;
  margin-bottom: 1rem;
  font-weight: initial;
}

.contentWrapper {
  background-color: var(--vd-color-grey-light-9);
  width: 100%;
  padding: 0.4rem;
  margin-bottom: 0.4rem;
  border-radius: 0.9rem;
}

.priceWrapper {
  width: 100%;
  margin-bottom: 0.4rem;
  border-radius: 0.9rem;
}

.rewardWrapper {
  margin-top: 0.5rem;
}

.rewardSection {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  min-height: 3.5rem;
  overflow-x: hidden;
  overflow-y: scroll;
  display: flex;
  justify-content: center;
}

.rewardIcon {
  height: 16px;
  width: 16px;
}
.tagWrapper {
  display: flex;
  width: 100%;
}
.rewardName {
  padding: 0.1rem;
  margin: 0.4rem;
  color: var(--vd-color-blue-dark-1) !important;
  font-size: 0.9rem;
  background-color: var(--vd-color-blue-light-9);
  border-radius: 0.3rem;
}

.rewardTooltip {
  padding: 0.2rem;
  width: 1.6rem;
  background-color: var(--vd-color-white);
  color: var(--vd-color-black) !important;
  border-radius: 0.4rem;
}

.tagWrapper {
  display: flex;
  width: 100%;
}

.tag {
  margin: 0.1rem;
  padding: 0.1rem 0.75rem;
  font-size: 1rem;
  background-color: var(--vd-color-white) !important;
}

.truncatedTag {
  max-width: 6.3rem;
}

.tagLabel {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0.1rem;
}
.actionRow {
  display: flex;
  justify-content: space-between;
  padding: 0 0.25rem;
  margin-bottom: 0.5rem;
  height: 10px;
  width: 100%;
}

.row {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
.column {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.blueChip {
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: var(--vd-color-blue-light-8);
  color: var(--vd-color-blue-dark-3);
  border-radius: 10px;
  padding: 3px 5px;
  font-size: 12px;
  font-weight: bolder;

  svg {
    font-size: 10px;
  }
}
</style>
